import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { mockProjects } from "@/mocks/projects";

const categoryColors: Record<string, string> = {
  Education: "bg-amber-100 text-amber-700",
  Infrastructure: "bg-sky-100 text-sky-700",
  Healthcare: "bg-rose-100 text-rose-700",
  Community: "bg-purple-100 text-purple-700",
  Humanitarian: "bg-orange-100 text-orange-700",
};

export default function ProjectsPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeStatus, setActiveStatus] = useState("all");

  const categories = [
    { key: "All", label: t("projectsPage.filters.all") },
    { key: "Education", label: t("projectsPage.filters.education") },
    { key: "Healthcare", label: t("projectsPage.filters.healthcare") },
    { key: "Infrastructure", label: t("projectsPage.filters.infrastructure") },
    { key: "Community", label: t("projectsPage.filters.community") },
    { key: "Humanitarian", label: t("projectsPage.filters.humanitarian") },
  ];

  const filtered = mockProjects.filter((p) => {
    const catMatch = activeCategory === "All" || p.category === activeCategory;
    const statusMatch = activeStatus === "all" || p.status === activeStatus;
    return catMatch && statusMatch;
  });

  return (
    <main className="pt-24">
      {/* Header */}
      <section className="py-14 bg-[#1B2838]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">{t("projectsPage.tag")}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("projectsPage.title")}
          </h1>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            {t("projectsPage.subtitle")}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-gray-100 sticky top-[100px] z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  activeCategory === cat.key
                    ? "bg-[#1B2838] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-1 bg-gray-100 rounded-full p-1">
            {[
              { value: "all", label: t("projectsPage.status.all") },
              { value: "active", label: t("projectsPage.status.active") },
              { value: "completed", label: t("projectsPage.status.completed") },
            ].map((s) => (
              <button
                key={s.value}
                onClick={() => setActiveStatus(s.value)}
                className={`px-4 py-1 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  activeStatus === s.value ? "bg-white text-[#1B2838]" : "text-gray-500"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-14 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <i className="ri-folder-open-line text-5xl mb-4 block"></i>
              <p>{t("projectsPage.empty")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((project) => {
                const pct = Math.round((project.raised / project.goal) * 100);
                return (
                  <div
                    key={project.id}
                    className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-[#4ECDC4]/40 transition-all duration-300 group flex flex-col"
                  >
                    <div className="relative h-48 sm:h-52 overflow-hidden flex-shrink-0">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[project.category] || "bg-gray-100 text-gray-600"}`}>
                          {project.category}
                        </span>
                      </div>
                      {project.status === "completed" && (
                        <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                          {t("projectsPage.badge.completed")}
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-bold text-[#1B2838] text-lg mb-2 leading-tight">{project.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1">{project.description}</p>

                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                          <span>{t("projectsPage.raised")}: <strong className="text-[#1B2838]">${project.raised.toLocaleString()}</strong> of ${project.goal.toLocaleString()}</span>
                          <span className="font-semibold text-[#4ECDC4]">{pct}%</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#4ECDC4] rounded-full"
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-map-pin-line"></i>
                            {project.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-group-line"></i>
                            {project.beneficiaries.toLocaleString()}
                          </span>
                        </div>
                        <Link
                          to={`/projects/${project.id}`}
                          className="text-[#4ECDC4] text-sm font-semibold hover:text-[#3dbdb5] cursor-pointer flex items-center gap-1"
                        >
                          {t("projectsPage.details")} <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}